import { apiClient } from '@/lib/generated/api-client'
import type { components } from '@/lib/generated/api-types'

// API response types matching the real API response
type Project = components['schemas']['Project']
type ProjectSite = components['schemas']['ProjectSite']
type ProjectSiteReview = components['schemas']['ProjectSiteReview']
type ProjectSiteReviewAttachment = components['schemas']['ProjectSiteReviewAttachment']

// Enhanced response interface with all related data
interface ProjectWithRelatedData extends Project {
	sites_data?: ProjectSite[]
	reviews_data?: ProjectSiteReview[]
	attachments_data?: ProjectSiteReviewAttachment[]
}

interface ProjectApiResponse {
	success: boolean
	data: ProjectWithRelatedData
	message: string
}

// Helper function to fetch all project sites using the optimized endpoint
const fetchProjectSites = async (projectUuid: string): Promise<ProjectSite[]> => {
	try {
		const response = await apiClient.GET('/api/v1/projects/{project_uuid}/sites/', {
			params: {
				path: {
					project_uuid: projectUuid,
				},
			},
		})

		if ('error' in response) {
			console.warn(`Failed to fetch project sites for ${projectUuid}:`, (response as any).error)
			return []
		}

		// Handle paginated response structure
		return response.data?.results || response.data || []
	} catch (error) {
		console.warn(`Error fetching project sites for ${projectUuid}:`, error)
		return []
	}
}

// Helper function to fetch all project reviews for all sites using the optimized endpoint
const fetchProjectReviews = async (
	projectUuid: string,
	sites: ProjectSite[],
): Promise<ProjectSiteReview[]> => {
	if (sites.length === 0) return []

	// Fetch reviews for all sites in parallel
	const reviewPromises = sites.map(async (site) => {
		try {
			const response = await apiClient.GET(
				'/api/v1/projects/{project_uuid}/sites/{site_id}/reviews/',
				{
					params: {
						path: {
							project_uuid: projectUuid,
							site_id: site.id,
						},
					},
				},
			)

			if ('error' in response) {
				console.warn(`Failed to fetch reviews for site ${site.id}:`, (response as any).error)
				return []
			}

			// Handle paginated response structure
			return response.data?.results || response.data || []
		} catch (error) {
			console.warn(`Error fetching reviews for site ${site.id}:`, error)
			return []
		}
	})

	const allReviews = await Promise.all(reviewPromises)
	return allReviews.flat()
}

// Helper function to fetch all review attachments for all reviews using the optimized endpoint
const fetchReviewAttachments = async (
	projectUuid: string,
	reviews: ProjectSiteReview[],
): Promise<ProjectSiteReviewAttachment[]> => {
	if (reviews.length === 0) return []

	// Fetch attachments for all reviews in parallel
	const attachmentPromises = reviews.map(async (review) => {
		try {
			const response = await apiClient.GET(
				'/api/v1/projects/{project_uuid}/sites/{site_id}/reviews/{review_id}/attachments/',
				{
					params: {
						path: {
							project_uuid: projectUuid,
							site_id: review.site,
							review_id: review.id,
						},
					},
				},
			)

			if ('error' in response) {
				console.warn(
					`Failed to fetch attachments for review ${review.id}:`,
					(response as any).error,
				)
				return []
			}

			// Handle paginated response structure
			return response.data?.results || response.data || []
		} catch (error) {
			console.warn(`Error fetching attachments for review ${review.id}:`, error)
			return []
		}
	})

	const allAttachments = await Promise.all(attachmentPromises)
	return allAttachments.flat()
}

// Enhanced API call to fetch project with all related data using optimized endpoints
const fetchProjectById = async (projectId: string): Promise<ProjectApiResponse> => {
	try {
		// Fetch main project data
		const projectResponse = await apiClient.GET('/api/v1/projects/{uuid}/', {
			params: {
				path: {
					uuid: projectId,
				},
			},
		})

		if ('error' in projectResponse) {
			throw new Error(`Failed to fetch project: ${(projectResponse as any).error}`)
		}

		if (!projectResponse.data) {
			throw new Error(`Project not found with id: ${projectId}`)
		}

		const project = projectResponse.data

		const sitesData = await fetchProjectSites(projectId)

		const reviewsData = await fetchProjectReviews(projectId, sitesData)
		const attachmentsData = await fetchReviewAttachments(projectId, reviewsData)

		// Combine all data
		const projectWithRelatedData: ProjectWithRelatedData = {
			...project,
			sites_data: sitesData,
			reviews_data: reviewsData,
			attachments_data: attachmentsData,
		}

		return {
			success: true,
			data: projectWithRelatedData,
			message: 'Project with related data fetched successfully',
		}
	} catch (error) {
		throw new Error(
			`Error fetching project: ${error instanceof Error ? error.message : 'Unknown error'}`,
		)
	}
}

// Project filters interface
export interface ProjectFilters {
	actuator?: string
	duration?: number
	duration__gte?: number
	duration__lte?: number
	ebc__gte?: number
	ebc__lte?: number
	ept__gte?: number
	ept__lte?: number
	grower?: string
	name?: string
	observer?: string
	page?: number
	page_size?: number
	project_type?: 'CARB' | 'MARI' | 'OTHE' | 'TERR'
	rbc__gte?: number
	rbc__lte?: number
	rpt__gte?: number
	rpt__lte?: number
	start_year?: number
	status?: 'DRAFT' | 'SUBMI' | 'UN_VA' | 'REJEC' | 'ONGOI' | 'ENDED' | 'CLOSE'
}

// Function to fetch all projects with optional filters
const fetchAllProjects = async (
	filters?: ProjectFilters,
): Promise<{
	success: boolean
	data: Project[]
	message: string
	count?: number
	page_size?: number
	current_page?: number
	num_pages?: number
	next?: string
	previous?: string
}> => {
	try {
		const response = await apiClient.GET('/api/v1/projects/', {
			params: {
				query: filters,
			},
		})

		if (!response.data) {
			throw new Error('No projects data received')
		}

		// Handle paginated response structure
		const projects = response.data.results || response.data || []

		return {
			success: true,
			data: projects,
			message: 'Projects fetched successfully',
			count: (response.data as any).count,
			page_size: (response.data as any).page_size || 10,
			current_page: (response.data as any).current_page || 1,
			num_pages: (response.data as any).num_pages || 1,
			next: (response.data as any).next || undefined,
			previous: (response.data as any).previous || undefined,
		}
	} catch (error) {
		throw new Error(
			`Error fetching projects: ${error instanceof Error ? error.message : 'Unknown error'}`,
		)
	}
}

// Function to fetch all projects in review with complete data
const fetchAllProjectsInReview = async (
	filters?: ProjectFilters,
): Promise<{
	success: boolean
	data: Project[]
	message: string
	count?: number
	page_size?: number
	current_page?: number
	num_pages?: number
	next?: string
	previous?: string
}> => {
	try {
		const response = await apiClient.GET('/api/v1/projects/projects-in-review/', {
			params: {
				query: filters,
			},
		})

		if (!response.data) {
			throw new Error('No projects in review data received')
		}

		// Handle paginated response structure
		const projects = response.data.results || response.data || []

		return {
			success: true,
			data: projects,
			message: 'Projects in review fetched successfully',
			count: (response.data as any).count,
			page_size: (response.data as any).page_size || 10,
			current_page: (response.data as any).current_page || 1,
			num_pages: (response.data as any).num_pages || 1,
			next: (response.data as any).next || undefined,
			previous: (response.data as any).previous || undefined,
		}
	} catch (error) {
		throw new Error(
			`Error fetching projects in review: ${error instanceof Error ? error.message : 'Unknown error'}`,
		)
	}
}

// Export the fetch functions for use in loaders if needed
export { fetchProjectById, fetchAllProjects, fetchAllProjectsInReview }
